import {
  Column,
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  Header,
  PaginationState,
  Row,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslatedNormalizedErrors } from '../backend/use-translated-errors';
import { Pagination } from '../../visual-components/pagination/pagination';
import { PAGINATION_DEFAULT_PAGE_SIZE } from './constants';

type Props<T> = {
  children: (props: ChildProps<T>) => React.ReactNode;
  // https://github.com/TanStack/table/issues/4382
  // there's an issue with the typing of ColumnDef
  columns: ColumnDef<T, any>[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  data: T[];
  hasError?: boolean;
  error?: unknown;
  tableIdentifier?: string;
  pageSize?: number;
  isLoading?: boolean;
};

type ChildProps<T> = {
  visibleColumns: Column<T, unknown>[];
  headers: Header<T, unknown>[];
  rows: Row<T>[];
  footers: Header<T, unknown>[];
  hasError: boolean;
  errorMessage: undefined | string;
  isLoading?: boolean;
  paginationComponent?: React.ReactNode;
};

export const DataTable = <T,>({
  children,
  columns,
  data,
  pageSize = PAGINATION_DEFAULT_PAGE_SIZE,
  hasError = false,
  error,
  isLoading = false,
}: Props<T>) => {
  const visibilityState: VisibilityState = useMemo(
    () => Object.fromEntries(columns.map((col) => [col.id, !col.meta?.isHidden])),
    [columns],
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });

  const { getFirstOptionalErrorMessage } = useTranslatedNormalizedErrors();

  const table = useReactTable({
    data,
    columns,
    sortDescFirst: false,
    initialState: {
      columnVisibility: visibilityState,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
    enableHiding: true,
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  useEffect(() => {
    table.setColumnVisibility(visibilityState);
  }, [visibilityState, table]);

  const allColumns = table.getAllColumns();
  const visibleColumns = allColumns.filter((col) => !col.columnDef.meta?.isHidden);

  const headers = table.getHeaderGroups()[0].headers;

  const rows = table.getRowModel().rows;

  const footers = table.getFooterGroups()[0].headers;

  const hasPagination = table.getPageCount() > 1;

  let paginationComponent: React.ReactNode | undefined;
  if (hasPagination) {
    // our pagination is 1 indexed and tanstack react table pagination is 0
    // indexed
    const setPage = (page: number) => {
      table.setPageIndex(page - 1);
    };
    const currentPage = pagination.pageIndex + 1;
    paginationComponent = <Pagination currentPage={currentPage} lastPage={table.getPageCount()} setPage={setPage} />;
  }

  const errorMessage = getFirstOptionalErrorMessage(error);

  return children({
    visibleColumns,
    headers,
    rows,
    footers,
    isLoading,
    hasError,
    errorMessage,
    paginationComponent,
  });
};
